import moment from "moment";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { ReactComponent as Arrow } from "../../../assets/newImages/icons/arrowLeft.svg";
import BannersCanva from "../../../components/BannersCanva/BannersCanva";
import BannersShowcase from "../../../components/BannersShowcase/BannersShowcase";
import { Button } from "../../../components/Button";
import Intermediadores from "../../../components/Configuracoes/Intermediadores/Intermediadores";
import PagamentoCombinar from "../../../components/Configuracoes/PagamentoCombinar/PagamentoCombinar";
import Dropdown from "../../../components/Dropdowns/Dropdown";
import { FooterPaginas } from "../../../components/FooterPaginas/FooterPaginas";
import Form from "../../../components/Form";
import { Input } from "../../../components/Input";
import Titulo from "../../../components/Modal/ModaisConfigsPrimeiroAcesso/Titulo/Titulo";
import Modal from "../../../components/Modal/Modal";
import { Select } from "../../../components/Select";
import SkeletonCatalogo from "../../../components/Skeletons/SkeletonCatalogo/SkeletonCatalogo";
import Tabs from "../../../components/Tabs/Tabs";
import { Toggle } from "../../../components/Toggle/Toggle";
import { ToggleConfigurar } from "../../../components/Toggle/ToggleConfigurar/ToggleConfigurar";
import Table from "../../../components/Table/Table";
import { CatalogoContext } from "../../../contexts/Catalogo/CatalogoContext";
import { ConfiguracoesContext } from "../../../contexts/Configuracoes/ConfiguracoesContext";
import { EmpresaContext } from "../../../contexts/Empresa/EmpresaContext";
import { useTheme } from "../../../contexts/Theme/ThemeContext";
import { boolean, deepCopy, staginEnv } from "../../../services/Functions";
import { currencyMask, percentageMask } from "../../../services/Masks";
import { unmaskPercentage, unmaskToNumbers } from "../../../services/Unmasks";
import { AjusteCategoria, AjusteVariacao, DescontoProgressivo } from "../../../types/Catalogo";
import Produtos from "./Produtos/Produtos";
import ProdutosPromocao from "./ProdutosPromocao/ProdutosPromocao";
import "./styles";
import { ContainerCatalogo, ContainerReplicarCatalogo } from "./styles";
import LockedIcon from "../../../components/LockedIcon/LockedIcon";
import { ProdutosProvider } from "../../../contexts/Produtos/ProdutosProvider";
import { dropshippingApi } from "../../../hooks/dropshippingApi";

interface CatalogosProps {
  permissoesPlanos: string[];
}

interface AjustePrecoInputsProps {
  tipo: string;
  direcao: string;
  valor: string;
  onTipoChange: (v: string) => void;
  onDirecaoChange: (v: string) => void;
  onValorChange: (v: string) => void;
  tipoLabel?: string;
  replicado: boolean;
  moedaSelecionada: string;
}

const AjustePrecoInputs: React.FC<AjustePrecoInputsProps> = ({ tipo, direcao, valor, onTipoChange, onDirecaoChange, onValorChange, tipoLabel = "Tipo de ajuste", replicado, moedaSelecionada }) => (
  <div className="ajustePrecoInputs">
    <div>
      <Select
        value={tipo}
        onChange={onTipoChange}
        label={tipoLabel}
        sizeLabel="medium"
        disabled={replicado}
        options={[
          { value: 'porcentagem', name: 'Porcentagem' },
          { value: 'valor_fixo', name: 'Valor fixo' },
          { value: 'valor_final', name: 'Valor final' },
        ]}
      />
    </div>
    <div>
      <Select
        value={direcao}
        onChange={onDirecaoChange}
        label="Ajuste"
        sizeLabel="medium"
        disabled={replicado || tipo === 'valor_final'}
        options={[
          { value: 'aumentar', name: 'Aumentar' },
          { value: 'diminuir', name: 'Diminuir' },
        ]}
      />
    </div>
    <div>
      {tipo === 'porcentagem' ? (
        <Input
          type="text"
          value={valor}
          label="Porcentagem"
          placeholder="0,0%"
          disabled={replicado}
          mask={{
            mask: percentageMask,
            unmask: unmaskPercentage,
          }}
          onChange={onValorChange}
        />
      ) : (
        <Input
          type="text"
          value={valor}
          label={`Valor (${moedaSelecionada})`}
          placeholder={`${moedaSelecionada} 0,00`}
          disabled={replicado}
          mask={{
            mask: (value: string) => currencyMask(value, moedaSelecionada),
            unmask: unmaskToNumbers,
          }}
          onChange={onValorChange}
        />
      )}
    </div>
  </div>
);

const Catalogos: React.FC<CatalogosProps> = ({permissoesPlanos}) => {
  const { theme } = useTheme();


  useEffect(() => {

    if(permissoesPlanos.length != 0){
      setLoadingPermissoes(false);
    }
  }
  , [permissoesPlanos]);

  // DROPSHIPPING PROVISÓRIO: revendedor com chave ativa usa pagamento e frete da mãe, sem ajuste por catálogo
  const [dropshippingRevendedorAtivo, setDropshippingRevendedorAtivo] = useState(false);
  const dropshippingSatelitePermitido = !!(permissoesPlanos as any)["DROPSHIPPING_CONTA_SATELITE"]?.permitido;
  useEffect(() => {
    if (!dropshippingSatelitePermitido) return;
    dropshippingApi()
      .configuracao()
      .then((config) => setDropshippingRevendedorAtivo(!!config.satelite?.vinculo?.ativo))
      .catch((error) => console.log(error));
  }, [dropshippingSatelitePermitido]);

  const {
    removerCatalogo,
    salvarCatalogo,
    catalogo,
    setCatalogo,
    loading,
    setLoading,
    outrosUsuarios,
    init: initCatalogos,
    listasDePrecos,
    empresaIntermediadores,
    descontosProgressivos,
    setDescontosProgressivos,
    ajustesCategoria,
    setAjustesCategoria,
    ajustesVariacao,
    setAjustesVariacao,
    categorias,
  } = useContext(CatalogoContext);
  const { empresa, setEmpresa } = useContext(EmpresaContext);
  const {
    configuracoes: configuracoesEmpresa,
    fretes_especiais: fretesEspeciaisEmpresa,
    fretes_gratis: fretesGratisEmpresa,
  } = useContext(ConfiguracoesContext);

  const navigate = useNavigate();

  const [loadingSalvar, setLoadingSalvar] = useState(false);
  const limiteDeBanners = 3;

  const [loadingPermissoes, setLoadingPermissoes] = useState(true);
  const [showErros, setShowErrors] = useState(false);

  const [formValid, setFormValid] = useState(false);

  const [usuariosCatalogoAuxiliar, setUsuariosCatalogoAuxiliar] = useState<any>(
    []
  );

  const [activeTab, setActiveTab] = useState("informacoes");
  // Aba da coluna da direita: "Produtos | catálogo" ou "Banners".
  const [tabConteudo, setTabConteudo] = useState<"produtos" | "banners">("produtos");
  // Aba da direita enquanto edita um ajuste por produto: "Produtos | promoção" ou "Banners".
  const [tabPromocao, setTabPromocao] = useState<"produtos-promocao" | "banners">(
    "produtos-promocao"
  );

  const [configsPagamentoCombinar, setConfigsPagamentoCombinar] =
    useState<any>(false);
  const [configsPagamentosEspeciais, setConfigsPagamentosEspeciais] =
    useState<any>(false);
  const [configsFretesEspeciais, setConfigsFretesEspeciais] =
    useState<boolean>(false);
  const [configsManutencao, setConfigsManutencao] = useState<boolean>(false);
  const [configsBloqueioSenha, setConfigsBloqueioSenha] = useState<boolean>(false);

  const [configsMinimoCompra, setConfigsMinimoCompra] = useState<boolean>(false);
  const [configsAjustePreco, setConfigsAjustePreco] = useState<boolean>(false);
  const [configsDescontoProgressivo, setConfigsDescontoProgressivo] = useState<boolean>(false);

  // Estados para formulário de novo desconto progressivo
  const [novoDesconto, setNovoDesconto] = useState<{
    de: string;
    ate: string;
    desconto: string;
  }>({
    de: '',
    ate: '',
    desconto: ''
  });
  const [editandoIndex, setEditandoIndex] = useState<number | null>(null);

  // Estados para formulário de ajuste por categoria
  const [novoAjusteCategoria, setNovoAjusteCategoria] = useState<{
    id_categoria: string;
    ajuste_tipo: 'porcentagem' | 'valor_fixo' | 'valor_final';
    ajuste_direcao: 'aumentar' | 'diminuir';
    ajuste_valor: string;
  }>({
    id_categoria: '',
    ajuste_tipo: 'porcentagem',
    ajuste_direcao: 'diminuir',
    ajuste_valor: ''
  });
  const [editandoAjusteCategoriaIndex, setEditandoAjusteCategoriaIndex] = useState<number | null>(null);

  // Estados para formulário de ajuste por produto (variação)
  const [novoAjusteProduto, setNovoAjusteProduto] = useState<{
    ajuste_tipo: 'porcentagem' | 'valor_fixo' | 'valor_final';
    ajuste_direcao: 'aumentar' | 'diminuir';
    ajuste_valor: string;
  }>({
    ajuste_tipo: 'porcentagem',
    ajuste_direcao: 'diminuir',
    ajuste_valor: ''
  });
  const [editandoAjusteProdutoIndex, setEditandoAjusteProdutoIndex] = useState<number | null>(null);
  const [produtosPromocaoAtual, setProdutosPromocaoAtual] = useState<number[]>([]);
  const [variacoesPromocaoAtual, setVariacoesPromocaoAtual] = useState<{
    [id_produto: number]: number[];
  }>({});

  const [modalReplicarCatalogo, setModalReplicarCatalogo] = useState(false);

  const [tabs, setTabs] = useState([
    { name: "Informações", value: "informacoes" },
    { name: "Promoções", value: "promocoes" },
    // {name: "Avançado", value: "avancado"},
  ]);

  const handleSalvarCatalogo = async () => {
    // Bloqueio ativo sem senha de 6 caracteres deixaria o catálogo inacessível.
    if (
      catalogo.configuracoes?.bloqueioSenha?.ativo &&
      (catalogo.configuracoes?.bloqueioSenha?.senha ?? "").length !== 6
    ) {
      setActiveTab("avancado");
      Swal.fire({
        title: "Senha obrigatória",
        text: "Defina uma senha de 6 caracteres para o bloqueio do catálogo, ou desative a opção.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    // Fluxo em dois passos: o "Salvar e continuar" primeiro leva o cliente para a
    // etapa de Promoções + Banners; só a partir dela o catálogo é salvo de fato.
    // Banner segue opcional — este gate é só de navegação.
    if (activeTab !== "promocoes") {
      setActiveTab("promocoes");
      setTabConteudo("banners");
      return;
    }

    // Banner é opcional, mas o que está ativo precisa das duas artes — mesma regra
    // do "Salvar banner". Aqui fechamos a brecha de salvar direto sem concluir o
    // banner. Inativo não vai para a loja, então pode ficar incompleto.
    const bannerIncompleto = catalogo.banners.find(
      (banner) =>
        banner.ativo &&
        Boolean(banner.imagem_desktop) !== Boolean(banner.imagem_mobile)
    );
    if (bannerIncompleto) {
      setTabConteudo("banners");
      Swal.fire({
        title: "Arte obrigatória",
        text: bannerIncompleto.imagem_desktop
          ? "Um banner tem só a arte para computadores. Envie também a de celulares (640 x 320px)."
          : "Um banner tem só a arte para celulares. Envie também a de computadores (1400 x 175px).",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    setLoadingSalvar(true);
    try {
      const identificacao = await salvarCatalogo();
      if (identificacao) {
        navigator.clipboard
          .writeText(`${empresa!.dominio_https}${identificacao}${staginEnv()}`)
          .then(
            (res) => {
              Swal.fire({
                title: "Catálogo salvo com sucesso",
                text: "Link copiado para a área de transferência!",
                icon: "success",
                showConfirmButton: false,
                timer: 1250,
              });
            },
            (err) => {
              Swal.fire({
                title: "Catálogo salvo com sucesso",
                icon: "success",
                showConfirmButton: false,
                timer: 1250,
              });
            }
          );
      } else {
        Swal.fire({
          title: "Catálogo salvo com sucesso",
          icon: "success",
          showConfirmButton: false,
          timer: 1250,
        });
      }
      navigate("/painel/catalogos");
    } catch (error: any) {
      if (error.response.data.error === "LIMITE_CATALOGOS_EXCEDIDO") {
        Swal.fire({
          title: "Limite de catálogos excedido",
          text: "Você atingiu o limite de catálogos para a sua conta",
          icon: "error",
          confirmButtonText: "Ok",
        });

        initCatalogos();
      } else {
        Swal.fire({
          title: "Erro ao salvar catálogo",
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    }
    setLoadingSalvar(false);
  };

  const handleAtivarPagamentoEspecial = (newValue: boolean) => {
    let newCatalogo = deepCopy(catalogo);
    if (newValue) {
      newCatalogo.intermediadores = {
        ...empresaIntermediadores,
      };

      if (
        !empresaIntermediadores ||
        !empresaIntermediadores.a_combinar === undefined
      ) {
        //se as configs nao tem intermediador, cria o a combinar pro catalogo
        newCatalogo.intermediadores.a_combinar = {
          ativo: false,
          prazo_contato: "1",
        };
      }
      setConfigsPagamentosEspeciais(true);
    } else {
      // se desativar, remove os intermediadores do catalogo
      newCatalogo.intermediadores = {};
      setConfigsPagamentosEspeciais(false);
    }
    setCatalogo(newCatalogo);
  };

  const handleAtivarFretesEspeciais = (newValue: boolean) => {
    let newCatalogo = deepCopy(catalogo);

    if (newValue) {
      // Se já existe configuração de fretes, apenas ativa
      if (newCatalogo.configuracoes.fretesEspeciais) {
        newCatalogo.configuracoes.fretesEspeciais.ativo = true;
      } else {
        // Cria nova configuração com todos os fretes ativos (usando o id real do banco)
        const fretesGratisAtivos = fretesGratisEmpresa
          .filter((f: any) => f.ativo)
          .map((f: any) => ({ id: f.id, ativo: true }));

        const fretesEspeciaisAtivos = fretesEspeciaisEmpresa
          .filter((f: any) => f.ativo)
          .map((f: any) => ({ id: f.id, ativo: true }));

        // Cria array de opções de Correios ativas
        const correiosOpcoesAtivas = (configuracoesEmpresa?.frete?.opcoes || [])
          .filter((opcao) => opcao.ativo)
          .map((opcao) => ({ codigo: opcao.codigo, ativo: true }));

        newCatalogo.configuracoes.fretesEspeciais = {
          ativo: true,
          correios: {
            opcoes: correiosOpcoesAtivas,
          },
          melhorEnvio: configuracoesEmpresa?.frete?.melhorEnvio?.ativo ?? false,
          entregaLocal: configuracoesEmpresa?.frete?.entregaLocal?.ativo ?? false,
          retiradaNoLocal: configuracoesEmpresa?.frete?.retirada_no_local?.ativo ?? false,
          fretesGratis: fretesGratisAtivos,
          fretesEspeciais: fretesEspeciaisAtivos,
        };
      }
      setConfigsFretesEspeciais(true);
    } else {
      // Apenas desativa, mantendo as configurações
      if (newCatalogo.configuracoes.fretesEspeciais) {
        newCatalogo.configuracoes.fretesEspeciais.ativo = false;
      }
      setConfigsFretesEspeciais(false);
    }
    setCatalogo(newCatalogo);
  };

  const handleAtivarManutencao = (newValue: boolean) => {
    const newCatalogo = {
      ...catalogo,
      data_fim_manutencao: newValue
        ? moment().add(1, "days").format("YYYY/MM/DD HH:mm")
        : null,
    };
    setCatalogo(newCatalogo);
    setConfigsManutencao(newValue);
  };

  const onAdicionarProdutos = useCallback(
    (ids_produtos: number[]) => {
      setCatalogo((prevCatalogo) => {
        if (prevCatalogo.id_catalogo_original !== "0" && "") {
          return prevCatalogo;
        }
        const produtos = [...prevCatalogo.produtos];
        ids_produtos.forEach((id) => {
          if (!produtos.some((produto) => produto === id)) {
            produtos.push(id);
          }
        });
        return {
          ...prevCatalogo,
          produtos,
        };
      });
    },
    [setCatalogo]
  );

  const onRemoverProdutos = useCallback(
    (ids_produtos: number[]) => {
      setCatalogo((prevCatalogo) => {
        if (prevCatalogo.id_catalogo_original !== "0" && "") {
          return prevCatalogo;
        }
        const produtos = [...prevCatalogo.produtos];
        ids_produtos.forEach((id) => {
          const index = produtos.findIndex((p) => p === id);
          if (index === -1) return;

          produtos.splice(index, 1);
        });
        return {
          ...prevCatalogo,
          produtos,
        };
      });
    },
    [setCatalogo]
  );
  useEffect(() => {
  if (showErros && !formValid && (!catalogo.descricao || catalogo.descricao.trim() === "")) {
    Swal.fire({
      title: "Atenção!",
      text: "Você precisa adicionar um título para seu catálogo.",
      icon: "warning",
      confirmButtonText: "Ok",
    });
  }
}, [showErros, formValid, catalogo.descricao]);

  const handleExcluirCatalogo = () => {
    Swal.fire({
      title: "Tem certeza que deseja excluir este catálogo?",
      text: "Esta ação não poderá ser desfeita!",
      icon: "warning",
      cancelButtonText: "Cancelar",
      confirmButtonText: "Continuar",
      showCancelButton: true,
      customClass: {
        confirmButton: "btn-confirm",
        cancelButton: "btn-cancel",
        icon: "custom-swal-icon",
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Confirme sua ação",
          text: "Após confirmar a exclusão, a ação não pode ser desfeita.",
          icon: "warning",
          confirmButtonText: "Confirmar (5)",
          cancelButtonText: "Cancelar",
          showCancelButton: true,
          customClass: {
            confirmButton: "btn-confirm2",
            popup: "custom-swal-popup",
            icon: "custom-swal-icon",
            title: "custom-swal-title",
          },
          didOpen: () => {
            const confirmButton = Swal.getConfirmButton();
            if (confirmButton) {
              confirmButton.disabled = true;
              let secondsLeft = 5;
              const timerInterval = setInterval(() => {
                secondsLeft--;
                confirmButton.innerText = `Confirmar (${secondsLeft})`;
                if (secondsLeft <= 0) {
                  confirmButton.disabled = false;
                  confirmButton.innerText = "Confirmar";
                }
              }, 1000);
            }
          },
        }).then(async (secondResult) => {
          if (secondResult.isConfirmed) {
            removerCatalogo();
          }
        });
      }
    });
  };

  const memoizedProdutosCatalogo = useMemo(
    () => catalogo.produtos,
    [catalogo.produtos]
  );
  const memoizedAdicionarTodosProdutos = useMemo(
    () => catalogo.configuracoes.adicionarTodosProdutos,
    [catalogo.configuracoes.adicionarTodosProdutos]
  );

  // Tabela de preço selecionada
  const tabelaSelecionada = useMemo(() => {
    return listasDePrecos.find(
      (lista) => lista.id === catalogo.id_tabela
    );
  }, [listasDePrecos, catalogo.id_tabela]);

  const moedaSelecionada = tabelaSelecionada?.moeda || "R$";

  // Contagem de produtos com promoção
  const produtosComPromocao = useMemo(() => {
    if (!boolean(catalogo.ajuste_preco_ativo)) return 0;
    const escopo = catalogo.ajuste_preco_escopo || 'catalogo';
    if (escopo === 'catalogo') return catalogo.produtos.length;
    if (escopo === 'categoria') {
      return ajustesCategoria
        .filter(a => a.ativo)
        .reduce((sum, a) => sum + (a.qtd_produtos || 0), 0);
    }
    if (escopo === 'produto') {
      const ids = new Set<number>();
      ajustesVariacao.filter(a => a.ativo).forEach(a => a.produtos.forEach(id => ids.add(id)));
      return ids.size;
    }
    return 0;
  }, [catalogo.ajuste_preco_ativo, catalogo.ajuste_preco_escopo, catalogo.produtos.length, ajustesCategoria, ajustesVariacao]);

  const { produtosEmPromocao, variacoesEmOutraPromocao } = useMemo(() => {
    const produtos = new Set<number>();
    const variacoes: { [id_produto: number]: number[] } = {};

    ajustesVariacao.forEach((a, idx) => {
      if (!a.ativo || idx === editandoAjusteProdutoIndex) return;

      a.produtos.forEach(id => {
        const parciais = a.variacoes_por_produto?.[id];
        if (!parciais || !parciais.length) {
          produtos.add(id);
          return;
        }
        variacoes[id] = [...(variacoes[id] || []), ...parciais];
      });
    });

    return { produtosEmPromocao: produtos, variacoesEmOutraPromocao: variacoes };
  }, [ajustesVariacao, editandoAjusteProdutoIndex]);

  // Próximo ID de grupo para ajustes de variação
  const proximoGrupoAjuste = useMemo(() => {
    if (!ajustesVariacao.length) return 1;
    return Math.max(...ajustesVariacao.map(a => a.ajuste_grupo)) + 1;
  }, [ajustesVariacao]);

  // Funções auxiliares para desconto progressivo
  const getUltimoAte = useCallback(() => {
    const ativos = descontosProgressivos?.filter(d => d.ativo) || [];
    if (ativos.length === 0) return 0;
    return Math.max(...ativos.map(d => parseFloat(d.ate) || 0));
  }, [descontosProgressivos]);

  const validarProgressao = useCallback((de: number, ate: number, indexEditando: number | null) => {
    if (ate <= de) return false;

    const ativos = descontosProgressivos?.filter(d => d.ativo) || [];

    // Se não há regras ativas, qualquer valor é válido
    if (ativos.length === 0 || (ativos.length === 1 && indexEditando !== null)) return true;

    if (indexEditando !== null) {
      // Editando: verificar regra anterior e próxima
      const regraAnterior = descontosProgressivos
        .filter((d, i) => d.ativo && i < indexEditando)
        .sort((a, b) => parseFloat(b.ate) - parseFloat(a.ate))[0];

      const regraProxima = descontosProgressivos
        .filter((d, i) => d.ativo && i > indexEditando)
        .sort((a, b) => parseFloat(a.de) - parseFloat(b.de))[0];

      // De deve ser >= Até da regra anterior
      if (regraAnterior && de <= parseFloat(regraAnterior.ate)) return false;

      // Até deve ser <= De da próxima regra
      if (regraProxima && ate >= parseFloat(regraProxima.de)) return false;

      return true;
    } else {
      // Criando nova: De deve ser >= último Até
      const ultimoAte = Math.max(...ativos.map(d => parseFloat(d.ate) || 0));
      return de >= ultimoAte;
    }
  }, [descontosProgressivos]);

  const podeReativar = useCallback((index: number, desconto: DescontoProgressivo) => {
    const ativos = descontosProgressivos.filter((d, i) => d.ativo && i !== index);
    const deNum = parseFloat(desconto.de);
    const ateNum = parseFloat(desconto.ate);

    for (const ativo of ativos) {
      const ativoDe = parseFloat(ativo.de);
      const ativoAte = parseFloat(ativo.ate);
      // Verifica sobreposição
      if (!(ateNum <= ativoDe || deNum >= ativoAte)) {
        return false;
      }
    }
    return true;
  }, [descontosProgressivos]);

  const handleCriarDesconto = () => {
    const de = parseFloat(novoDesconto.de) || 0;
    const ate = parseFloat(novoDesconto.ate) || 0;
    const desconto = parseFloat(novoDesconto.desconto) || 0;

    if (!novoDesconto.de || !novoDesconto.ate || !novoDesconto.desconto) {
      Swal.fire({
        title: "Campos obrigatórios",
        text: "Preencha todos os campos para criar o desconto.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (!validarProgressao(de, ate, editandoIndex)) {
      const ultimoAteFormatado = catalogo.desconto_progressivo_tipo === 'valor'
        ? currencyMask(String(getUltimoAte()), moedaSelecionada)
        : getUltimoAte();
      Swal.fire({
        title: "Faixa inválida",
        text: editandoIndex === null
          ? `O campo "De" deve ser maior ou igual a ${ultimoAteFormatado} e "Até" deve ser maior que "De".`
          : `O campo "Até" deve ser maior que "De".`,
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    if (editandoIndex !== null) {
      // Editando existente
      const novosDescontos = [...descontosProgressivos];
      novosDescontos[editandoIndex] = {
        ...novosDescontos[editandoIndex],
        de: novoDesconto.de,
        ate: novoDesconto.ate,
        desconto: novoDesconto.desconto,
      };
      setDescontosProgressivos(novosDescontos);
      setEditandoIndex(null);
    } else {
      // Criando novo
      const novoItem: DescontoProgressivo = {
        de: novoDesconto.de,
        ate: novoDesconto.ate,
        desconto: novoDesconto.desconto,
        ativo: true,
      };
      setDescontosProgressivos([...descontosProgressivos, novoItem]);
    }

    // Limpar formulário - se criou novo, preenche "De" com Até + 1
    const proximoDe = editandoIndex === null
      ? String(parseFloat(novoDesconto.ate) + 1)
      : '';
    setNovoDesconto({
      de: proximoDe,
      ate: '',
      desconto: ''
    });
  };

  const handleEditarDesconto = (index: number) => {
    const desconto = descontosProgressivos[index];
    setNovoDesconto({
      de: String(desconto.de),
      ate: String(desconto.ate),
      desconto: String(desconto.desconto),
    });
    setEditandoIndex(index);
  };

  const handleToggleDescontoAtivo = (index: number, novoValor: boolean) => {
    if (novoValor && !podeReativar(index, descontosProgressivos[index])) {
      Swal.fire({
        title: "Não é possível reativar",
        text: "Esta faixa de desconto sobrepõe outras faixas ativas. Edite a faixa antes de reativá-la.",
        icon: "warning",
        confirmButtonText: "Ok",
      });
      return;
    }

    const novosDescontos = [...descontosProgressivos];
    novosDescontos[index] = {
      ...novosDescontos[index],
      ativo: novoValor,
    };
    setDescontosProgressivos(novosDescontos);
  };

  // "replicado" já desabilita os campos (catálogo é cópia). Também tratamos como somente-leitura quando
  // o backend diz que o usuário não pode editar ESTE catálogo (pode_editar=false) — EXCETO ao duplicar,
  // que cria um catálogo NOVO do próprio usuário e por isso deve ser editável. Editor de hoje
  // (não-customizado, dono, geral) tem pode_editar=true → replicado fica idêntico ao atual.
  const replicado = !!(
    (catalogo.id_catalogo_original && catalogo.id_catalogo_original !== "0") ||
    (catalogo.pode_editar === false && !catalogo.duplicado)
  );

  const escopo = catalogo.ajuste_preco_escopo || 'catalogo';

  const ajusteInputProps = (() => {
    if (escopo === 'categoria') {
      return {
        tipo: novoAjusteCategoria.ajuste_tipo,
        direcao: novoAjusteCategoria.ajuste_direcao,
        valor: novoAjusteCategoria.ajuste_valor,
        tipoLabel: "Tipo de ajuste",
        onTipoChange: (v: string) => setNovoAjusteCategoria({ ...novoAjusteCategoria, ajuste_tipo: v as 'porcentagem' | 'valor_fixo' | 'valor_final' }),
        onDirecaoChange: (v: string) => setNovoAjusteCategoria({ ...novoAjusteCategoria, ajuste_direcao: v as 'aumentar' | 'diminuir' }),
        onValorChange: (v: string) => setNovoAjusteCategoria({ ...novoAjusteCategoria, ajuste_valor: v }),
        replicado,
        moedaSelecionada,
      };
    }
    if (escopo === 'produto') {
      return {
        tipo: novoAjusteProduto.ajuste_tipo,
        direcao: novoAjusteProduto.ajuste_direcao,
        valor: novoAjusteProduto.ajuste_valor,
        tipoLabel: "Tipo de ajuste",
        onTipoChange: (v: string) => setNovoAjusteProduto({ ...novoAjusteProduto, ajuste_tipo: v as 'porcentagem' | 'valor_fixo' | 'valor_final' }),
        onDirecaoChange: (v: string) => setNovoAjusteProduto({ ...novoAjusteProduto, ajuste_direcao: v as 'aumentar' | 'diminuir' }),
        onValorChange: (v: string) => setNovoAjusteProduto({ ...novoAjusteProduto, ajuste_valor: v }),
        replicado,
        moedaSelecionada,
      };
    }
    return {
      tipo: catalogo.ajuste_preco_tipo || 'porcentagem',
      direcao: catalogo.ajuste_preco_direcao || 'diminuir',
      valor: catalogo.ajuste_preco_valor ? String(catalogo.ajuste_preco_valor) : '',
      tipoLabel: "Tipo de desconto",
      onTipoChange: (v: string) => setCatalogo({ ...catalogo, ajuste_preco_tipo: v as 'porcentagem' | 'valor_fixo' | 'valor_final' }),
      onDirecaoChange: (v: string) => setCatalogo({ ...catalogo, ajuste_preco_direcao: v as 'aumentar' | 'diminuir' }),
      onValorChange: (v: string) => setCatalogo({ ...catalogo, ajuste_preco_valor: v }),
      replicado,
      moedaSelecionada,
    };
  })();

  const isEditando = escopo === 'categoria'
    ? editandoAjusteCategoriaIndex !== null
    : editandoAjusteProdutoIndex !== null;

  const ajusteButtonDisabled = (() => {
    if (escopo === 'categoria') {
      return replicado || !novoAjusteCategoria.id_categoria || !novoAjusteCategoria.ajuste_valor;
    }
    if (escopo === 'produto') {
      return replicado || !novoAjusteProduto.ajuste_valor;
    }
    return true;
  })();

  const handleCriarAjuste = () => {
    if (escopo === 'categoria') {
      const categoriaInfo = categorias.find(
        c => String(c.id) === novoAjusteCategoria.id_categoria
      );

      const novoAjuste: AjusteCategoria = {
        id_categoria: novoAjusteCategoria.id_categoria,
        nome_categoria: categoriaInfo?.descricao || '',
        qtd_produtos: categoriaInfo?.qtd_produtos || 0,
        ajuste_tipo: novoAjusteCategoria.ajuste_tipo,
        ajuste_direcao: novoAjusteCategoria.ajuste_tipo === 'valor_final'
          ? 'diminuir'
          : novoAjusteCategoria.ajuste_direcao,
        ajuste_valor: novoAjusteCategoria.ajuste_valor,
        ativo: true,
      };

      if (editandoAjusteCategoriaIndex !== null) {
        const novosAjustes = [...ajustesCategoria];
        novosAjustes[editandoAjusteCategoriaIndex] = {
          ...novosAjustes[editandoAjusteCategoriaIndex],
          ...novoAjuste,
        };
        setAjustesCategoria(novosAjustes);
        setEditandoAjusteCategoriaIndex(null);
      } else {
        setAjustesCategoria([...ajustesCategoria, novoAjuste]);
      }

      setNovoAjusteCategoria({
        id_categoria: '',
        ajuste_tipo: 'porcentagem',
        ajuste_direcao: 'diminuir',
        ajuste_valor: '',
      });
    } else if (escopo === 'produto') {
      if (produtosPromocaoAtual.length === 0) {
        const produtosSection = document.querySelector('.right .container-conteudo');
        if (produtosSection) {
          produtosSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
        return;
      }

      const variacoesDoAjuste: { [id_produto: number]: number[] } = {};
      produtosPromocaoAtual.forEach((id_produto) => {
        const variacoes = variacoesPromocaoAtual[id_produto];
        if (variacoes && variacoes.length) variacoesDoAjuste[id_produto] = variacoes;
      });

      if (editandoAjusteProdutoIndex !== null) {
        const novosAjustes = [...ajustesVariacao];
        novosAjustes[editandoAjusteProdutoIndex] = {
          ...novosAjustes[editandoAjusteProdutoIndex],
          ajuste_tipo: novoAjusteProduto.ajuste_tipo,
          ajuste_direcao: novoAjusteProduto.ajuste_tipo === 'valor_final'
            ? 'diminuir'
            : novoAjusteProduto.ajuste_direcao,
          ajuste_valor: novoAjusteProduto.ajuste_valor,
          produtos: produtosPromocaoAtual,
          variacoes_por_produto: variacoesDoAjuste,
        };
        setAjustesVariacao(novosAjustes);
        setEditandoAjusteProdutoIndex(null);
      } else {
        const novoAjuste = {
          ajuste_grupo: proximoGrupoAjuste,
          ajuste_tipo: novoAjusteProduto.ajuste_tipo,
          ajuste_direcao: novoAjusteProduto.ajuste_tipo === 'valor_final'
            ? 'diminuir' as const
            : novoAjusteProduto.ajuste_direcao,
          ajuste_valor: novoAjusteProduto.ajuste_valor,
          ativo: true,
          variacoes: [],
          produtos: produtosPromocaoAtual,
          variacoes_por_produto: variacoesDoAjuste,
        };
        setAjustesVariacao([...ajustesVariacao, novoAjuste]);
      }

      setNovoAjusteProduto({
        ajuste_tipo: 'porcentagem',
        ajuste_direcao: 'diminuir',
        ajuste_valor: ''
      });
      setProdutosPromocaoAtual([]);
      setVariacoesPromocaoAtual({});
    }
  };

  const ajustesTableConfig = (() => {
    if (escopo === 'categoria') {
      return {
        title: "Ajustes de preço por categoria",
        columns: [
          {
            key: "categoria",
            label: "Categoria",
            mask: (value: string) => <span className="fonteTexto">{value}</span>,
          },
          {
            key: "ajuste",
            label: "Tipo de ajuste",
            mask: (value: string) => <span className="fonteTexto">{value}</span>,
          },
          {
            key: "qtd_produtos",
            label: "Qtd de produtos",
            mask: (value: string) => <span className="fonteTexto">{value}</span>,
          },
          {
            key: "situacao",
            label: "Situação",
            mask: (value: any) => value,
          },
        ],
        data: ajustesCategoria.map((ajuste, index) => {
          const valor = Number(ajuste.ajuste_valor || 0);
          let ajusteFormatado = '';
          const direcaoTexto = ajuste.ajuste_direcao === 'aumentar' ? 'Aumentar' : 'Diminuir';
          if (ajuste.ajuste_tipo === 'porcentagem') {
            const percentual = (valor / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            ajusteFormatado = `${direcaoTexto} ${percentual}%`;
          } else if (ajuste.ajuste_tipo === 'valor_fixo') {
            const valorFormatado = (valor / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            ajusteFormatado = `${direcaoTexto} ${moedaSelecionada} ${valorFormatado}`;
          } else {
            const valorFormatado = (valor / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            ajusteFormatado = `Valor final ${moedaSelecionada} ${valorFormatado}`;
          }
          return {
            categoria: ajuste.nome_categoria,
            ajuste: ajusteFormatado,
            qtd_produtos: `${ajuste.qtd_produtos} produto${ajuste.qtd_produtos !== 1 ? 's' : ''}`,
            situacao: (
              <div className="acoes-desconto">
                <Toggle
                  disabled={replicado}
                  value={ajuste.ativo}
                  onChange={(newValue) => {
                    const novosAjustes = [...ajustesCategoria];
                    novosAjustes[index] = { ...novosAjustes[index], ativo: newValue };
                    setAjustesCategoria(novosAjustes);
                  }}
                />
                <span
                  className="editar-link fonteTexto"
                  onClick={() => {
                    setNovoAjusteCategoria({
                      id_categoria: String(ajuste.id_categoria),
                      ajuste_tipo: ajuste.ajuste_tipo,
                      ajuste_direcao: ajuste.ajuste_direcao,
                      ajuste_valor: String(ajuste.ajuste_valor),
                    });
                    setEditandoAjusteCategoriaIndex(index);
                  }}
                >
                  Editar
                </span>
              </div>
            ),
          };
        }),
      };
    }
    if (escopo === 'produto') {
      return {
        title: "Ajustes de preço por produto",
        columns: [
          {
            key: "itens",
            label: "Itens",
            mask: (value: string) => <span className="fonteTexto">{value}</span>,
          },
          {
            key: "ajuste",
            label: "Tipo de ajuste",
            mask: (value: string) => <span className="fonteTexto">{value}</span>,
          },
          {
            key: "situacao",
            label: "Situação",
            mask: (value: any) => value,
          },
        ],
        data: ajustesVariacao.map((ajuste, index) => {
          const tipoLabel = ajuste.ajuste_tipo === 'porcentagem' ? 'Porcentagem' :
            ajuste.ajuste_tipo === 'valor_fixo' ? 'Valor fixo' : 'Valor final';
          const direcaoLabel = ajuste.ajuste_direcao === 'aumentar' ? 'Aumentar' : 'Diminuir';
          const valorFormatado = ajuste.ajuste_tipo === 'porcentagem'
            ? percentageMask(String(ajuste.ajuste_valor))
            : currencyMask(String(ajuste.ajuste_valor), moedaSelecionada);

          const comSelecaoParcial = Object.keys(ajuste.variacoes_por_produto || {}).length;

          return {
            itens: `${ajuste.produtos.length} produto${ajuste.produtos.length !== 1 ? 's' : ''}${
              comSelecaoParcial
                ? ` (${comSelecaoParcial} com variações selecionadas)`
                : ''
            }`,
            ajuste: ajuste.ajuste_tipo === 'valor_final'
              ? `${tipoLabel}: ${valorFormatado}`
              : `${tipoLabel}: ${direcaoLabel} ${valorFormatado}`,
            situacao: (
              <div className="acoes-desconto">
                <Toggle
                  value={ajuste.ativo}
                  disabled={replicado}
                  onChange={(novoValor) => {
                    const novosAjustes = [...ajustesVariacao];
                    novosAjustes[index] = {
                      ...novosAjustes[index],
                      ativo: novoValor,
                    };
                    setAjustesVariacao(novosAjustes);
                  }}
                />
                <span
                  className="editar-link"
                  onClick={() => {
                    if (replicado) return;
                    setNovoAjusteProduto({
                      ajuste_tipo: ajuste.ajuste_tipo,
                      ajuste_direcao: ajuste.ajuste_direcao,
                      ajuste_valor: String(ajuste.ajuste_valor),
                    });
                    setProdutosPromocaoAtual(ajuste.produtos);
                    setVariacoesPromocaoAtual(ajuste.variacoes_por_produto || {});
                    setEditandoAjusteProdutoIndex(index);
                  }}
                >
                  Editar
                </span>
              </div>
            ),
          };
        }),
      };
    }
    return null;
  })();


  if(loadingPermissoes){
    return <SkeletonCatalogo />
  }

  // Enquanto o cliente edita um ajuste de preço por produto, a coluna da direita
  // vira o seletor "Produtos | promoção". Fora disso ela é o par Produtos/Banners.
  const editandoProdutosPromocao =
    activeTab === "promocoes" &&
    catalogo.ajuste_preco_escopo === "produto" &&
    configsAjustePreco &&
    boolean(catalogo.ajuste_preco_ativo);

  // Abas espelhadas: Informações ↔ Produtos | catálogo, Promoções ↔ Banners.
  const mostrarPainelProdutosBanners =
    (activeTab === "informacoes" || activeTab === "promocoes") &&
    !editandoProdutosPromocao;

  return (
    <>
      <ContainerCatalogo theme={theme}>
        {loading ? (
          <SkeletonCatalogo />
        ) : (
          <>
            <Modal
              isOpen={modalReplicarCatalogo}
              setModal={setModalReplicarCatalogo}
              width={"475px"}
              onUseEffect={() => {
                setUsuariosCatalogoAuxiliar([...catalogo.outros_usuarios]);
              }}
              onBackdropClose={() => {
                setCatalogo({
                  ...catalogo,
                  outros_usuarios: usuariosCatalogoAuxiliar,
                });
              }}
            >
              <ContainerReplicarCatalogo theme={theme}>
                <Titulo
                  titulo={"Replicar esse catálogo para outros usuários"}
                  subtitulo={
                    "Selecione os usuários que você deseja replicar esse catálogo"
                  }
                  flexDirection="column-reverse"
                />
                <div className="column-flex">
                  <div className="usuarios-container">
                    {outrosUsuarios
                      .filter(
                        (outroUsuario) =>
                          !usuariosCatalogoAuxiliar.some(
                            (catalogoUsuario) =>
                              catalogoUsuario.id === outroUsuario.id
                          )
                      )
                      .map((usuario) => {
                        return (
                          <>
                            <label className="checkboxBonito flex-label">
                              <input
                                type="checkbox"
                                checked={catalogo.outros_usuarios.some(
                                  (catalogoUsuario) =>
                                    catalogoUsuario.id === usuario.id
                                )}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setCatalogo({
                                      ...catalogo,
                                      outros_usuarios: [
                                        ...catalogo.outros_usuarios,
                                        usuario,
                                      ],
                                    });
                                  } else {
                                    setCatalogo({
                                      ...catalogo,
                                      outros_usuarios:
                                        catalogo.outros_usuarios.filter(
                                          (catalogoUsuario) =>
                                            catalogoUsuario.id !== usuario.id
                                        ),
                                    });
                                  }
                                }}
                              />
                              <span>{usuario.email}</span>
                            </label>
                          </>
                        );
                      })}
                  </div>
                  {outrosUsuarios.filter((outroUsuario) =>
                    usuariosCatalogoAuxiliar.some(
                      (catalogoUsuario) =>
                        catalogoUsuario.id === outroUsuario.id
                    )
                  ).length > 0 && (
                    <>
                      <Titulo
                        titulo={"Usuários que já possuem <br/> esse catálogo"}
                        subtitulo={
                          "Caso este catálogo sofra mudança, <br/> ela será replicada para os usuários abaixo."
                        }
                        flexDirection="column-reverse"
                      />
                      <div className="usuarios-container">
                        {outrosUsuarios
                          .filter((outroUsuario) =>
                            usuariosCatalogoAuxiliar.some(
                              (catalogoUsuario) =>
                                catalogoUsuario.id === outroUsuario.id
                            )
                          )
                          .map((usuario) => {
                            return (
                              <>
                                <label className="flex-label">
                                  <span>- {usuario.email}</span>
                                </label>
                              </>
                            );
                          })}
                      </div>
                    </>
                  )}
                </div>
                <div className="row-flex">
                  <div
                    className="arrow"
                    onClick={() => {
                      setModalReplicarCatalogo(false);
                      setCatalogo({
                        ...catalogo,
                        outros_usuarios: usuariosCatalogoAuxiliar,
                      });
                    }}
                  >
                    <Arrow />
                    Voltar
                  </div>
                  <Button
                    title="Salvar"
                    onClick={() => {
                      setModalReplicarCatalogo(false);
                    }}
                  ></Button>
                </div>
              </ContainerReplicarCatalogo>
            </Modal>
            <Form
              id={"form-cadastro-catalogo"}
              setFormValid={setFormValid}
              onSubmit={() => handleSalvarCatalogo()}
              scrollTimeout={350}
              onInvalid={(input) => {
                const container = input.closest('[data-tab]');
                if (container) {
                  const tab = container.getAttribute('data-tab');
                  if (tab && tab !== activeTab) {
                    setActiveTab(tab);
                  }
                }
              }}
            >
              <div className="grid-container-catalogo">
                <>
                  <div className="left">
                    <Tabs
                      tabs={tabs}
                      value={activeTab}
                      onChange={(newValue) => {
                        setActiveTab(newValue);
                        if (newValue !== "avancado") {
                          setTabs(
                            tabs.filter((tab) => tab.value !== "avancado")
                          );
                        }
                        // Espelha a coluna da direita: Informações → Produtos,
                        // Promoções → Banners.
                        if (newValue === "informacoes") setTabConteudo("produtos");
                        else if (newValue === "promocoes") setTabConteudo("banners");
                      }}
                    />
                    <div
                      data-tab="informacoes"
                      className={`container-conteudo ${
                        activeTab === "informacoes" && "container-ativo"
                      }`}
                    >
                      <div>
                        <div className="bold fonteTexto">Título</div>
                        <Input
                          type={"text"}
                          maxLength={95}
                          showMaxLength={true}
                          value={catalogo.descricao}
                          label="O título aparecerá na parte superior do seu catálogo e ao compartilhar o link dele para seus clientes."
                          placeholder={"Título do seu catálogo"}
                          required={true}
                          disabled={replicado}
                          onChange={(newValue) => {
                              let texto = newValue;
                              texto = texto.trimStart();
                              const invalidos = /[.,\/?&#=%+\"'<>\:\\]/;

                              if (invalidos.test(texto.charAt(0))) {
                                texto = texto.substring(1);
                              }
                            setCatalogo({
                              ...catalogo,
                              descricao: texto,
                            });
                          }}
                        ></Input>
                      </div>
                      <div>
                        <Select
                          value={catalogo.id_tabela}
                          onChange={(newValue) => {
                            setCatalogo({
                              ...catalogo,
                              id_tabela: newValue,
                            });
                          }}
                          label="Tabela de preço"
                          sizeLabel="medium"
                          placeholder="Tabela de preço"
                          disabled={replicado}
                          required={true}
                          options={listasDePrecos}
                        />
                      </div>

                      {boolean(catalogo.principal) === false && (
                        <>
                          <div className="flex">
                            <label>
                              <Toggle
                                disabled={
                                  replicado ||
                                  !permissoesPlanos[
                                    "AGENDAR_EXPIRACAO_DO_CATALOGO"
                                  ]?.permitido
                                }
                                value={
                                  moment(
                                    catalogo.data_vencimento,
                                    "YYYY/MM/DD HH:mm"
                                  ).isSameOrAfter(
                                    moment("2100-01-01", "YYYY/MM/DD HH:mm")
                                  ) || catalogo.data_vencimento === ""
                                    ? false
                                    : true
                                }
                                onChange={(newValue) => {
                                  if (newValue) {
                                    setCatalogo({
                                      ...catalogo,
                                      data_vencimento: moment()
                                        .add(1, "days")
                                        .format("YYYY/MM/DD HH:mm"),
                                    });
                                  } else {
                                    setCatalogo({
                                      ...catalogo,
                                      data_vencimento: moment(
                                        "2100-01-01 00:00",
                                        "YYYY/MM/DD HH:mm"
                                      ).format("YYYY/MM/DD HH:mm"),
                                    });
                                  }
                                }}
                              />
                              <span className="fonteTexto">Agendar expiração</span>
                              {!permissoesPlanos[
                                "AGENDAR_EXPIRACAO_DO_CATALOGO"
                              ]?.permitido && (
                                <LockedIcon mensagem="Recurso disponível a partir do plano Essencial." />
                              )}
                            </label>
                          </div>
                          <Dropdown
                            active={
                              !(
                                moment(
                                  catalogo.data_vencimento,
                                  "YYYY/MM/DD HH:mm"
                                ).isSameOrAfter(
                                  moment("2100-01-01", "YYYY/MM/DD HH:mm")
                                ) || catalogo.data_vencimento === ""
                              )
                            }
                            animated={true}
                          >
                            <Input
                              label="Ao selecionar data e horário para expiração, seu catálogo, a partir da data selecionada, não ficará mais disponível para seus clientes."
                              type="datetime-local"
                              disabled={replicado}
                              onChange={(newValue) => {
                                setCatalogo({
                                  ...catalogo,
                                  data_vencimento: moment(
                                    newValue,
                                    "YYYY-MM-DDTHH:mm"
                                  ).format("YYYY/MM/DD HH:mm"),
                                });
                              }}
                              value={moment(
                                catalogo.data_vencimento,
                                "YYYY-MM-DD HH:mm"
                              ).format("YYYY-MM-DDTHH:mm")}
                              placeholder="2023-04-27 11:28"
                            />
                          </Dropdown>
                        </>
                      )}
                    </div>

                    {/* ------------------- promoções --------------------------------- */}
                    <div
                      data-tab="promocoes"
                      className={`container-conteudo ${
                        activeTab === "promocoes" && "container-ativo"
                      }`}
                    >
                      <div className="promocoes-container">
                        {/* Toggle 1: Mínimo de Compra */}
                        <div className="flex space-between no-wrap promocao">
                          <span className="fonteTexto">
                            Mínimo de Compra
                            {permissoesPlanos?.[
                              "OPCAO_DE_MINIMO_DE_COMPRA_NO_CATALOGO"
                            ]?.permitido === false && (
                              <LockedIcon mensagem="Recurso disponível a partir do plano Essencial" />
                            )}
                          </span>
                          <ToggleConfigurar
                            disabled={
                              replicado ||
                              permissoesPlanos?.[
                                "OPCAO_DE_MINIMO_DE_COMPRA_NO_CATALOGO"
                              ]?.permitido === false
                            }
                            nome={""}
                            value={boolean(catalogo.minimo_ativo)}
                            onChange={(newValue) => {
                              setCatalogo({
                                ...catalogo,
                                minimo_ativo: newValue,
                              });
                              if (newValue) {
                                setConfigsMinimoCompra(true);
                              }
                            }}
                            onConfigurar={() => {
                              setConfigsMinimoCompra(!configsMinimoCompra);
                            }}
                          />
                        </div>
                        <Dropdown
                          active={configsMinimoCompra && boolean(catalogo.minimo_ativo)}
                          animated={true}
                        >
                          <div className="minimoCompraInputs">
                            <div>
                              <Input
                                type={"text"}
                                value={catalogo.minimo_valor}
                                label={`Por valor`}
                                placeholder={`R$ 0,00`}
                                mask={{
                                  mask: (value: string) => currencyMask(value, moedaSelecionada),
                                  unmask: unmaskToNumbers,
                                }}
                                disabled={replicado}
                                onChange={(newValue) =>
                                  setCatalogo({
                                    ...catalogo,
                                    minimo_valor: newValue,
                                  })
                                }
                              />
                            </div>
                            <div>
                              <Input
                                type={"number"}
                                value={catalogo.minimo_quantidade}
                                label="Por quantidade"
                                placeholder={"0"}
                                disabled={replicado}
                                onChange={(newValue) => {
                                  if (newValue === "") newValue = "0";
                                  if (
                                    Number.parseInt(newValue) >= 0 &&
                                    Number.parseInt(newValue) < 999999
                                  )
                                    setCatalogo({
                                      ...catalogo,
                                      minimo_quantidade: newValue,
                                    });
                                }}
                              />
                            </div>
                          </div>
                        </Dropdown>

                        {/* Toggle 2: Desconto Progressivo */}
                        <div className="linha" />
                        <div className="flex space-between no-wrap promocao">
                          <span className="fonteTexto">
                            Desconto Progressivo
                            {!permissoesPlanos[
                              "DESCONTO_PROGRESSIVO_NO_CATALOGO"
                            ]?.permitido && (
                              <LockedIcon mensagem="Recurso disponível a partir do plano Avançado" />
                            )}
                          </span>
                          <ToggleConfigurar
                            disabled={
                              replicado ||
                              !permissoesPlanos[
                                "DESCONTO_PROGRESSIVO_NO_CATALOGO"
                              ]?.permitido
                            }
                            nome={""}
                            value={boolean(catalogo.desconto_progressivo_ativo)}
                            onChange={(newValue) => {
                              setCatalogo({
                                ...catalogo,
                                desconto_progressivo_ativo: newValue,
                              });
                              if (newValue) {
                                setConfigsDescontoProgressivo(true);
                              }
                            }}
                            onConfigurar={() => {
                              setConfigsDescontoProgressivo(!configsDescontoProgressivo);
                            }}
                          />
                        </div>
                        <Dropdown
                          active={configsDescontoProgressivo && boolean(catalogo.desconto_progressivo_ativo)}
                          animated={true}
                        >
                          {(() => {
                            const isTipoQuantidade = catalogo.desconto_progressivo_tipo === 'quantidade';

                            return (
                              <div className="descontoProgressivoContainer">
                                <Select
                                  value={catalogo.desconto_progressivo_tipo}
                                  onChange={(newValue) => {
                                    const novoTipo = newValue as 'quantidade' | 'valor';

                                    if (descontosProgressivos.length > 0 && novoTipo !== catalogo.desconto_progressivo_tipo) {
                                      Swal.fire({
                                        title: "Alterar tipo de desconto?",
                                        text: "Ao mudar o tipo, todas as regras existentes serão apagadas pois os valores não são compatíveis.",
                                        icon: "warning",
                                        showCancelButton: true,
                                        confirmButtonText: "Sim, apagar regras",
                                        cancelButtonText: "Cancelar",
                                      }).then((result) => {
                                        if (result.isConfirmed) {
                                          setDescontosProgressivos([]);
                                          setCatalogo({ ...catalogo, desconto_progressivo_tipo: novoTipo });
                                          setNovoDesconto({ de: '', ate: '', desconto: '' });
                                          setEditandoIndex(null);
                                        }
                                      });
                                    } else {
                                      setCatalogo({ ...catalogo, desconto_progressivo_tipo: novoTipo });
                                      setNovoDesconto({ ...novoDesconto, de: '', ate: '' });
                                    }
                                  }}
                                  label="Tipo de desconto"
                                  sizeLabel="medium"
                                  disabled={replicado}
                                  options={[
                                    { value: 'quantidade', name: 'Quantidade' },
                                    { value: 'valor', name: 'Valor' },
                                  ]}
                                />

                                <div className="descontoProgressivoInputs">
                                  <div>
                                    <Input
                                      key={`de-${catalogo.desconto_progressivo_tipo}`}
                                      type="text"
                                      value={novoDesconto.de}
                                      label="De"
                                      placeholder={isTipoQuantidade ? "0" : `${moedaSelecionada} 0,00`}
                                      disabled={replicado}
                                      mask={isTipoQuantidade ? undefined : {
                                        mask: (v: string) => currencyMask(v, moedaSelecionada),
                                        unmask: unmaskToNumbers
                                      }}
                                      onChange={(newValue) => {
                                        setNovoDesconto({ ...novoDesconto, de: isTipoQuantidade ? newValue.replace(/\D/g, '') : newValue });
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <Input
                                      key={`ate-${catalogo.desconto_progressivo_tipo}`}
                                      type="text"
                                      value={novoDesconto.ate}
                                      label="Até"
                                      placeholder={isTipoQuantidade ? "0" : `${moedaSelecionada} 0,00`}
                                      disabled={replicado}
                                      mask={isTipoQuantidade ? undefined : {
                                        mask: (v: string) => currencyMask(v, moedaSelecionada),
                                        unmask: unmaskToNumbers
                                      }}
                                      onChange={(newValue) => {
                                        setNovoDesconto({ ...novoDesconto, ate: isTipoQuantidade ? newValue.replace(/\D/g, '') : newValue });
                                      }}
                                    />
                                  </div>
                                  <div>
                                    <Input
                                      type="text"
                                      value={novoDesconto.desconto}
                                      label="Desconto"
                                      placeholder="0,00%"
                                      disabled={replicado}
                                      mask={{
                                        mask: percentageMask,
                                        unmask: unmaskPercentage
                                      }}
                                      onChange={(newValue) => {
                                        setNovoDesconto({ ...novoDesconto, desconto: newValue });
                                      }}
                                    />
                                  </div>
                                </div>

                                <div className="botao-criar-container">
                                  <Button
                                    title={editandoIndex !== null ? "Salvar ajuste" : "Criar desconto"}
                                    disabled={replicado}
                                    onClick={handleCriarDesconto}
                                  />
                                </div>

                                {/* Tabela de regras criadas */}
                                {descontosProgressivos.length > 0 && (
                                  <>
                                    <div className="linhaRegras" />
                                    <span className="tituloRegras fonteTexto">Regras de promoções criadas</span>
                                    <div className="descontoProgressivoTable">
                                      <Table
                                        columns={[
                                          {
                                            key: "faixa",
                                            label: isTipoQuantidade ? "Faixa de quantidade" : "Faixa de preço",
                                            mask: (value: string) => (
                                              <span className="fonteTexto">{value}</span>
                                            )
                                          },
                                          {
                                            key: "desconto",
                                            label: "Desconto",
                                            mask: (value: string) => (
                                              <span className="fonteTexto">{value}</span>
                                            )
                                          },
                                          {
                                            key: "situacao",
                                            label: "Situação",
                                            mask: (value: any) => value
                                          }
                                        ]}
                                        data={descontosProgressivos.map((desconto, index) => {
                                          const formatFaixa = (val: string | number) =>
                                            catalogo.desconto_progressivo_tipo === 'valor'
                                              ? currencyMask(String(val), moedaSelecionada)
                                              : String(val);
                                          return {
                                            faixa: `${formatFaixa(desconto.de)} - ${formatFaixa(desconto.ate)}`,
                                            desconto: `${(parseFloat(desconto.desconto) / 100).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`,
                                            situacao: (
                                              <div className="acoes-desconto">
                                                <Toggle
                                                  disabled={replicado}
                                                  value={desconto.ativo}
                                                  onChange={(newValue) => handleToggleDescontoAtivo(index, newValue)}
                                                />
                                                <span
                                                  className="editar-link fonteTexto"
                                                  onClick={() => handleEditarDesconto(index)}
                                                >
                                                  Editar
                                                </span>
                                              </div>
                                            )
                                          };
                                        })}
                                        sortable={false}
                                        selectable={false}
                                        button={false}
                                        page={1}
                                        itensPorPagina={-1}
                                      />
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })()}
                        </Dropdown>

                        {/* Toggle 3: Ajuste de Preço */}
                        <div className="linha" />
                        <div className="flex space-between no-wrap promocao">
                          <span className="fonteTexto">
                            Ajuste de Preço | De/Por
                            {!permissoesPlanos[
                              "AJUSTE_DE_PRECO_POR_PORCENTAGEM_NO_CATALOGO"
                            ]?.permitido && (
                              <LockedIcon mensagem="Recurso disponível a partir do plano Essencial" />
                            )}
                          </span>
                          <ToggleConfigurar
                            disabled={
                              replicado ||
                              !permissoesPlanos[
                                "AJUSTE_DE_PRECO_POR_PORCENTAGEM_NO_CATALOGO"
                              ]?.permitido
                            }
                            nome={""}
                            value={boolean(catalogo.ajuste_preco_ativo)}
                            onChange={(newValue) => {
                              setCatalogo({
                                ...catalogo,
                                ajuste_preco_ativo: newValue,
                              });
                              if (newValue) {
                                setConfigsAjustePreco(true);
                              }
                            }}
                            onConfigurar={() => {
                              setConfigsAjustePreco(!configsAjustePreco);
                            }}
                          />
                        </div>
                        <Dropdown
                          active={configsAjustePreco && boolean(catalogo.ajuste_preco_ativo)}
                          animated={true}
                        >
                          <>
                            {/* Radio options para escopo */}
                            <div className="escopoAjustePreco">
                              <label>
                                <input
                                  type="radio"
                                  className="input-radio"
                                  name="escopoAjuste"
                                  checked={catalogo.ajuste_preco_escopo === 'catalogo'}
                                  disabled={replicado}
                                  onChange={() => {
                                    setCatalogo({
                                      ...catalogo,
                                      ajuste_preco_escopo: 'catalogo',
                                    });
                                  }}
                                />
                                <span className="fonteTexto">Todo o catálogo</span>
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  className="input-radio"
                                  name="escopoAjuste"
                                  checked={catalogo.ajuste_preco_escopo === 'categoria'}
                                  disabled={replicado}
                                  onChange={() => {
                                    setCatalogo({
                                      ...catalogo,
                                      ajuste_preco_escopo: 'categoria',
                                    });
                                  }}
                                />
                                <span className="fonteTexto">Por categoria</span>
                              </label>
                              <label>
                                <input
                                  type="radio"
                                  className="input-radio"
                                  name="escopoAjuste"
                                  checked={catalogo.ajuste_preco_escopo === 'produto'}
                                  disabled={replicado}
                                  onChange={() => {
                                    setCatalogo({
                                      ...catalogo,
                                      ajuste_preco_escopo: 'produto',
                                    });
                                  }}
                                />
                                <span className="fonteTexto">Por produto</span>
                              </label>
                            </div>

                            {/* Select de categoria (só categoria) */}
                            {escopo === 'categoria' && (
                              <div className="ajustePrecoInputs">
                                <div style={{ flex: 1 }}>
                                  <Select
                                    value={novoAjusteCategoria.id_categoria}
                                    onChange={(newValue) => {
                                      setNovoAjusteCategoria({
                                        ...novoAjusteCategoria,
                                        id_categoria: newValue,
                                      });
                                    }}
                                    label="Categoria"
                                    sizeLabel="medium"
                                    disabled={replicado}
                                    options={[
                                      { value: '', name: 'Selecione uma categoria' },
                                      ...categorias
                                        .filter(cat =>
                                          !ajustesCategoria.some((a, idx) =>
                                            String(a.id_categoria) === String(cat.id) &&
                                            a.ativo &&
                                            idx !== editandoAjusteCategoriaIndex
                                          )
                                        )
                                        .map(cat => ({
                                          value: String(cat.id),
                                          name: `${cat.descricao} (${cat.qtd_produtos} produtos)`,
                                        }))
                                    ]}
                                  />
                                </div>
                              </div>
                            )}

                            {/* Inputs de ajuste (todos os escopos) */}
                            <AjustePrecoInputs {...ajusteInputProps} />

                            {/* Exemplo de cálculo */}
                              <div className="exemplo fonteRotulos">
                                {ajusteInputProps.valor && ajusteInputProps.tipo === 'porcentagem'
                                  ? ajusteInputProps.direcao === 'aumentar'
                                    ? `Exemplo: Se o preço do produto é ${moedaSelecionada} 10,00, ele passará a ser ${moedaSelecionada} ${(
                                        10 * (1 + Number.parseFloat(ajusteInputProps.valor || '0') / 10000)
                                      ).toLocaleString("pt-BR", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`
                                    : `Exemplo: Se o preço do produto é ${moedaSelecionada} 10,00, ele passará a ser ${moedaSelecionada} ${(
                                        10 * (1 - Number.parseFloat(ajusteInputProps.valor || '0') / 10000)
                                      ).toLocaleString("pt-BR", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`
                                  : ajusteInputProps.valor && ajusteInputProps.tipo === 'valor_fixo'
                                    ? ajusteInputProps.direcao === 'aumentar'
                                      ? `Exemplo: Se o preço do produto é ${moedaSelecionada} 10,00, ele passará a ser ${moedaSelecionada} ${(
                                          10 + Number.parseFloat(ajusteInputProps.valor || '0') / 100
                                        ).toLocaleString("pt-BR", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}`
                                      : `Exemplo: Se o preço do produto é ${moedaSelecionada} 10,00, ele passará a ser ${moedaSelecionada} ${(
                                          10 - Number.parseFloat(ajusteInputProps.valor || '0') / 100
                                        ).toLocaleString("pt-BR", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}`
                                    : ajusteInputProps.valor && ajusteInputProps.tipo === 'valor_final'
                                      ? `Todos os produtos terão o preço de ${moedaSelecionada} ${(Number.parseFloat(ajusteInputProps.valor || '0') / 100).toLocaleString("pt-BR", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        })}`
                                      : `Insira um valor para ver um exemplo`}
                              </div>

                            {/* Botão criar/salvar (categoria e produto) */}
                            {escopo !== 'catalogo' && (
                              <div className="botao-criar-container">
                                <Button
                                  title={isEditando ? "Salvar alteração" : "Criar promoção"}
                                  disabled={ajusteButtonDisabled}
                                  onClick={handleCriarAjuste}
                                />
                              </div>
                            )}

                            {/* Mostrar preço original (todos os escopos) */}
                            <div className="flex">
                              <label>
                                <Toggle
                                  disabled={replicado}
                                  value={boolean(
                                    catalogo.configuracoes?.mostrarPrecoOriginal
                                  )}
                                  onChange={(newValue) => {
                                    setCatalogo({
                                      ...catalogo,
                                      configuracoes: {
                                        ...catalogo.configuracoes,
                                        mostrarPrecoOriginal: newValue,
                                      },
                                    });
                                  }}
                                />
                                <span className="fonteTexto">Mostrar preço original das variações</span>
                              </label>
                            </div>

                            <Dropdown
                              active={boolean(catalogo.configuracoes?.mostrarPrecoOriginal)}
                              animated={true}
                            >
                              <div className="flex subitem">
                                <label>
                                  <Toggle
                                    disabled={replicado}
                                    value={boolean(catalogo.configuracoes?.mostrarTagDesconto)}
                                    onChange={(newValue) => {
                                      setCatalogo({
                                        ...catalogo,
                                        configuracoes: {
                                          ...catalogo.configuracoes,
                                          mostrarTagDesconto: newValue,
                                        },
                                      });
                                    }}
                                  />
                                  <span className="fonteTexto">Mostrar tag de desconto</span>
                                </label>
                              </div>
                            </Dropdown>

                            {/* Tabela de ajustes (categoria e produto) */}
                            {ajustesTableConfig && ajustesTableConfig.data.length > 0 && (
                              <>
                                <div className="linhaRegras" />
                                <span className="tituloRegras fonteTexto">{ajustesTableConfig.title}</span>
                                <div className="descontoProgressivoTable">
                                  <Table
                                    columns={ajustesTableConfig.columns}
                                    data={ajustesTableConfig.data}
                                    sortable={false}
                                    selectable={false}
                                    button={false}
                                    page={1}
                                    itensPorPagina={-1}
                                  />
                                </div>
                              </>
                            )}
                          </>
                        </Dropdown>
                      </div>
                    </div>

                    {/* ------------------- avancado --------------------------------- */}
                    <div
                      data-tab="avancado"
                      className={`container-conteudo ${
                        activeTab === "avancado" && "container-ativo"
                      }`}
                    >
                      <div className="flex space-between no-wrap">
                        <span className="fonteTexto">
                          Bloquear catálogo com senha?
                          {!permissoesPlanos["BLOQUEAR_CATALOGO_COM_SENHA"]
                            ?.permitido && (
                            <LockedIcon mensagem="Recurso disponível no plano Premium." />
                          )}
                        </span>
                        <ToggleConfigurar
                          disabled={
                            replicado ||
                            !permissoesPlanos["BLOQUEAR_CATALOGO_COM_SENHA"]
                              ?.permitido
                          }
                          nome={""}
                          value={boolean(catalogo.configuracoes?.bloqueioSenha?.ativo)}
                          onChange={(newValue) => {
                            setCatalogo({
                              ...catalogo,
                              configuracoes: {
                                ...catalogo.configuracoes,
                                bloqueioSenha: {
                                  ...catalogo.configuracoes?.bloqueioSenha,
                                  ativo: newValue,
                                },
                              },
                            });
                            setConfigsBloqueioSenha(newValue);
                          }}
                          onConfigurar={() => {
                            setConfigsBloqueioSenha(!configsBloqueioSenha);
                          }}
                        />
                      </div>
                      <Dropdown
                        active={configsBloqueioSenha && boolean(catalogo.configuracoes?.bloqueioSenha?.ativo)}
                        animated={true}
                      >
                        <div>
                          <Input
                            type="text"
                            placeholder="Senha de 6 caracteres"
                            disabled={replicado}
                            maxLength={6}
                            minLength={6}
                            value={catalogo.configuracoes?.bloqueioSenha?.senha ?? ""}
                            onChange={(newValue) => {
                              setCatalogo({
                                ...catalogo,
                                configuracoes: {
                                  ...catalogo.configuracoes,
                                  bloqueioSenha: {
                                    ...catalogo.configuracoes?.bloqueioSenha,
                                    senha: newValue.replace(/[^a-zA-Z0-9]/g, ""),
                                  },
                                },
                              });
                            }}
                          />
                        </div>
                      </Dropdown>
                      <div className="linha" />
                      <div className="flex space-between no-wrap">
                        <div className="flex">
                          <span className="fonteTexto">Formas de pagamento especiais</span>
                        </div>
                        <ToggleConfigurar
                          disabled={replicado}
                          nome={""}
                          value={
                            catalogo.intermediadores &&
                            (catalogo.intermediadores["pagarme"] !==
                              undefined ||
                              catalogo.intermediadores["pagseguro"] !==
                                undefined ||
                              catalogo.intermediadores["mercadopago"] !==
                                undefined ||
                              catalogo.intermediadores["pagarmehub"] !==
                                undefined ||
                              catalogo.intermediadores["a_combinar"] !==
                                undefined)
                          }
                          onChange={handleAtivarPagamentoEspecial}
                          onConfigurar={() => {
                            setConfigsPagamentosEspeciais(
                              !configsPagamentosEspeciais
                            );
                          }}
                        />
                      </div>
                      <Dropdown
                        active={configsPagamentosEspeciais}
                        animated={true}
                      >
                        <>
                            {catalogo.intermediadores &&
                            catalogo.intermediadores["pagarmehub"] !==
                                undefined && (
                                <div className="intermediador">
                                    <div className="linha"></div>
                                    <span className="nome-intermediador fonteTexto">Stone</span>
                                    <Intermediadores
                                        intermediador="pagarmehub"
                                        configIn="catalogo"
                                        somenteAtivar={dropshippingRevendedorAtivo}
                                    />
                                </div>
                            )}

                          {catalogo.intermediadores &&
                            catalogo.intermediadores["pagarme"] !==
                              undefined && (
                                <div className="intermediador">
                                  <div className="linha"></div>
                                  <span className="nome-intermediador fonteTexto">Stone</span>
                                  <Intermediadores
                                    intermediador="pagarme"
                                    configIn="catalogo"
                                    somenteAtivar={dropshippingRevendedorAtivo}
                                  />
                                </div>
                            )}
                          {catalogo.intermediadores &&
                            catalogo.intermediadores["pagseguro"] !==
                              undefined && (
                                <div className="intermediador">
                                  <div className="linha"></div>
                                  <span className="nome-intermediador fonteTexto">PagBank</span>
                                  <Intermediadores
                                    intermediador="pagseguro"
                                    configIn="catalogo"
                                    somenteAtivar={dropshippingRevendedorAtivo}
                                  />
                                </div>
                            )}
                          {catalogo.intermediadores &&
                            catalogo.intermediadores["mercadopago"] !==
                              undefined && (
                                 <div className="intermediador">
                                  <div className="linha"></div>
                                  <span className="nome-intermediador fonteTexto">Mercado Pago</span>
                                  <Intermediadores
                                    intermediador="mercadopago"
                                    configIn="catalogo"
                                    somenteAtivar={dropshippingRevendedorAtivo}
                                  />
                                </div>
                            )}
                          {catalogo.intermediadores &&
                            catalogo.intermediadores["a_combinar"] !==
                              undefined && (
                              <div className="intermediador">
                                <div className="linha"></div>
                                <div className="flex space-between no-wrap">
                                  <span className="nome-intermediador fonteTexto">A Combinar</span>
                                  <ToggleConfigurar
                                    disabled={replicado}
                                    nome={""}
                                    value={
                                    catalogo.intermediadores.a_combinar?.ativo ??
                                    false
                                  }
                                  isValid={
                                    catalogo.intermediadores.a_combinar?.ativo &&
                                    !configsPagamentoCombinar &&
                                    !(
                                      catalogo.intermediadores.a_combinar
                                        ?.prazo_contato &&
                                      Number.parseInt(
                                        catalogo.intermediadores.a_combinar
                                          ?.prazo_contato
                                      ) > 0
                                    )
                                      ? false
                                      : true
                                  }
                                  invalidText="Configure o prazo para contato"
                                  onChange={(newValue) => {
                                    setConfigsPagamentoCombinar(newValue);
                                    setCatalogo(
                                      deepCopy(catalogo, {
                                        intermediadores: {
                                          a_combinar: { ativo: newValue },
                                        },
                                      })
                                    );
                                  }}
                                  onConfigurar={() => {
                                    setConfigsPagamentoCombinar(
                                      !configsPagamentoCombinar
                                    );
                                  }}
                                  />
                                </div>
                                <Dropdown
                                  active={
                                    (catalogo.intermediadores.a_combinar
                                      ?.ativo ??
                                      false) && configsPagamentoCombinar
                                  }
                                  animated={true}
                                >
                                  {/* Dropshipping: o prazo vem do fornecedor, o revendedor só liga/desliga */}
                                  <fieldset disabled={dropshippingRevendedorAtivo} style={{ border: 0, padding: 0, margin: 0, minWidth: 0, ...(dropshippingRevendedorAtivo ? { opacity: 0.6, pointerEvents: "none" } : {}) }}>
                                    <PagamentoCombinar configIn="catalogo" />
                                  </fieldset>
                                </Dropdown>
                              </div>
                            )}
                        </>
                      </Dropdown>

                      {/* Seção de Fretes Especiais */}
                      <div className="linha" />
                      <div className="flex space-between no-wrap">
                        <div className="flex">
                          <span className="fonteTexto">Usar formas de fretes especiais</span>
                        </div>
                        <ToggleConfigurar
                          disabled={replicado}
                          nome={""}
                          value={catalogo.configuracoes?.fretesEspeciais?.ativo ?? false}
                          onChange={handleAtivarFretesEspeciais}
                          onConfigurar={() => {
                            setConfigsFretesEspeciais(!configsFretesEspeciais);
                          }}
                        />
                      </div>
                      <Dropdown
                        active={configsFretesEspeciais && (catalogo.configuracoes?.fretesEspeciais?.ativo ?? false)}
                        animated={true}
                      >
                        <>
                          <div className="frete-especial">
                             <div className="linha"></div>
                          </div>
                          <div className="frete-especial">
                            <Table
                              columns={[
                                {
                                  key: "tipo",
                                  label: "Tipo",
                                  mask: (value: string) => (
                                    <span className="fonteTexto">{value}</span>
                                  )
                                },
                                {
                                  key: "descricao",
                                  label: "Descrição",
                                  mask: (value: string) => (
                                    <span className="fonteTexto">{value}</span>
                                  )
                                },
                                {
                                  key: "configuracao",
                                  label: "Configuração",
                                  mask: (value: string) => (
                                    <span className="fonteTexto">{value}</span>
                                  )
                                },
                                {
                                  key: "acoes",
                                  label: "Status",
                                  mask: (value: any) => value
                                }
                              ]}
                              data={[
                                // Correios
                                ...(configuracoesEmpresa?.frete?.correiosAtivo &&
                                  configuracoesEmpresa?.frete?.opcoes?.some((opcao) => opcao.ativo)
                                  ? configuracoesEmpresa.frete.opcoes
                                      .filter((opcao) => opcao.ativo)
                                      .map((opcao) => ({
                                        tipo: "Correios",
                                        descricao: opcao.descricao,
                                        configuracao: "-",
                                        acoes: (
                                          <Toggle
                                            disabled={replicado}
                                            value={
                                              catalogo.configuracoes?.fretesEspeciais?.correios?.opcoes?.find(
                                                (o) => o.codigo === opcao.codigo
                                              )?.ativo ?? false
                                            }
                                            onChange={(newValue) => {
                                              const opcoesAtuais = [
                                                ...(catalogo.configuracoes?.fretesEspeciais?.correios?.opcoes || []),
                                              ];
                                              const indexExistente = opcoesAtuais.findIndex(
                                                (o) => o.codigo === opcao.codigo
                                              );
                                              if (indexExistente >= 0) {
                                                opcoesAtuais[indexExistente].ativo = newValue;
                                              } else {
                                                opcoesAtuais.push({ codigo: opcao.codigo, ativo: newValue });
                                              }
                                              setCatalogo(
                                                deepCopy(catalogo, {
                                                  configuracoes: {
                                                    fretesEspeciais: {
                                                      correios: {
                                                        opcoes: opcoesAtuais,
                                                      },
                                                    },
                                                  },
                                                })
                                              );
                                            }}
                                          />
                                        )
                                      }))
                                  : []),
                                // Fretes Grátis
                                ...(configuracoesEmpresa?.frete?.freteGratisAtivo &&
                                  fretesGratisEmpresa.filter((f) => f.ativo).length > 0
                                  ? fretesGratisEmpresa
                                      .filter((f: any) => f.ativo)
                                      .map((frete: any) => {
                                        // Monta a descrição do destino
                                        let destino = "Brasil Inteiro";
                                        if (!frete.brasil_inteiro) {
                                          if (frete.por_estado?.ativo && frete.por_estado?.estados?.length > 0) {
                                            destino = frete.por_estado.estados.join(", ");
                                          } else if (frete.por_cep?.ativo) {
                                            destino = "Por CEP";
                                          }
                                        }

                                        // Monta a configuração (valor mínimo)
                                        const configuracao = frete.valor_minimo && parseFloat(frete.valor_minimo) > 0
                                          ? `Mín. ${currencyMask(frete.valor_minimo.toString())}`
                                          : "-";

                                        return {
                                          tipo: "Frete Grátis",
                                          descricao: destino,
                                          configuracao,
                                          acoes: (
                                            <Toggle
                                              disabled={replicado}
                                              value={
                                                catalogo.configuracoes?.fretesEspeciais?.fretesGratis?.find(
                                                  (fg) => fg.id === frete.id
                                                )?.ativo ?? false
                                              }
                                              onChange={(newValue) => {
                                                const fretesGratisAtuais = [
                                                  ...(catalogo.configuracoes?.fretesEspeciais?.fretesGratis || []),
                                                ];
                                                const indexExistente = fretesGratisAtuais.findIndex(
                                                  (fg) => fg.id === frete.id
                                                );
                                                if (indexExistente >= 0) {
                                                  fretesGratisAtuais[indexExistente].ativo = newValue;
                                                } else {
                                                  fretesGratisAtuais.push({ id: frete.id, ativo: newValue });
                                                }
                                                setCatalogo(
                                                  deepCopy(catalogo, {
                                                    configuracoes: {
                                                      fretesEspeciais: {
                                                        fretesGratis: fretesGratisAtuais,
                                                      },
                                                    },
                                                  })
                                                );
                                              }}
                                            />
                                          )
                                        };
                                      })
                                  : []),
                                // Fretes Especiais
                                ...(configuracoesEmpresa?.frete?.freteEspecialAtivo &&
                                  fretesEspeciaisEmpresa.filter((f: any) => f.ativo).length > 0
                                  ? fretesEspeciaisEmpresa
                                      .filter((f: any) => f.ativo)
                                      .map((frete: any) => {
                                        // Monta a configuração (faixa de CEP)
                                        const configuracao = frete.cep_inicio && frete.cep_fim
                                          ? `CEP ${frete.cep_inicio} - ${frete.cep_fim}`
                                          : "-";

                                        return {
                                          tipo: "Frete Especial",
                                          descricao: frete.descricao || "Frete Especial",
                                          configuracao,
                                          acoes: (
                                            <Toggle
                                              disabled={replicado}
                                              value={
                                                catalogo.configuracoes?.fretesEspeciais?.fretesEspeciais?.find(
                                                  (fe) => fe.id === frete.id
                                                )?.ativo ?? false
                                              }
                                              onChange={(newValue) => {
                                                const fretesEspeciaisAtuais = [
                                                  ...(catalogo.configuracoes?.fretesEspeciais?.fretesEspeciais || []),
                                                ];
                                                const indexExistente = fretesEspeciaisAtuais.findIndex(
                                                  (fe) => fe.id === frete.id
                                                );
                                                if (indexExistente >= 0) {
                                                  fretesEspeciaisAtuais[indexExistente].ativo = newValue;
                                                } else {
                                                  fretesEspeciaisAtuais.push({ id: frete.id, ativo: newValue });
                                                }
                                                setCatalogo(
                                                  deepCopy(catalogo, {
                                                    configuracoes: {
                                                      fretesEspeciais: {
                                                        fretesEspeciais: fretesEspeciaisAtuais,
                                                      },
                                                    },
                                                  })
                                                );
                                              }}
                                            />
                                          )
                                        };
                                      })
                                  : []),
                                // Melhor Envio
                                ...(configuracoesEmpresa?.frete?.melhorEnvio?.ativo ? [{
                                  tipo: "Melhor Envio",
                                  descricao: "Melhor Envio",
                                  configuracao: "-",
                                  acoes: (
                                    <Toggle
                                      disabled={replicado}
                                      value={catalogo.configuracoes?.fretesEspeciais?.melhorEnvio ?? false}
                                      onChange={(newValue) => {
                                        setCatalogo(
                                          deepCopy(catalogo, {
                                            configuracoes: {
                                              fretesEspeciais: {
                                                melhorEnvio: newValue,
                                              },
                                            },
                                          })
                                        );
                                      }}
                                    />
                                  )
                                }] : []),
                                // Entrega Local
                                ...(configuracoesEmpresa?.frete?.entregaLocal?.ativo ? [{
                                  tipo: "Entrega Local",
                                  descricao: "Entrega Local",
                                  configuracao: "-",
                                  acoes: (
                                    <Toggle
                                      disabled={replicado}
                                      value={catalogo.configuracoes?.fretesEspeciais?.entregaLocal ?? false}
                                      onChange={(newValue) => {
                                        setCatalogo(
                                          deepCopy(catalogo, {
                                            configuracoes: {
                                              fretesEspeciais: {
                                                entregaLocal: newValue,
                                              },
                                            },
                                          })
                                        );
                                      }}
                                    />
                                  )
                                }] : []),
                                // Retirada no Local
                                ...(configuracoesEmpresa?.frete?.retirada_no_local?.ativo ? [{
                                  tipo: "Retirada no Local",
                                  descricao: "Retirada no Local",
                                  configuracao: "-",
                                  acoes: (
                                    <Toggle
                                      disabled={replicado}
                                      value={catalogo.configuracoes?.fretesEspeciais?.retiradaNoLocal ?? false}
                                      onChange={(newValue) => {
                                        setCatalogo(
                                          deepCopy(catalogo, {
                                            configuracoes: {
                                              fretesEspeciais: {
                                                retiradaNoLocal: newValue,
                                              },
                                            },
                                          })
                                        );
                                      }}
                                    />
                                  )
                                }] : [])
                              ]}
                              sortable={false}
                              selectable={false}
                              button={false}
                              page={1}
                              itensPorPagina={-1}
                            />
                          </div>
                        </>
                      </Dropdown>

                      {outrosUsuarios &&
                        outrosUsuarios.length > 0 &&
                        catalogo.id !== "0" && (
                          <>
                            <div className="linha" />
                            <div className="flex space-between no-wrap">
                              <span className="fonteTexto">
                                Replicar este catálogo para outros usuários?
                                {!permissoesPlanos[
                                  "REPLICAR_CATALOGOS_ATRELADO_A_USUARIOS"
                                ]?.permitido && (
                                  <LockedIcon
                                    mensagem="Recurso disponível a partir do plano Essencial"
                                    className="locked"
                                  />
                                )}
                              </span>
                              <ToggleConfigurar
                                nome={""}
                                disabled={
                                  replicado ||
                                  !permissoesPlanos[
                                    "REPLICAR_CATALOGOS_ATRELADO_A_USUARIOS"
                                  ]?.permitido
                                }
                                value={
                                  catalogo.outros_usuarios &&
                                  catalogo.outros_usuarios.length > 0
                                }
                                onChange={(newValue) =>
                                  newValue
                                    ? setModalReplicarCatalogo(true)
                                    : setCatalogo(
                                        deepCopy(catalogo, {
                                          outros_usuarios: [],
                                        })
                                      )
                                }
                                onConfigurar={() => {
                                  setModalReplicarCatalogo(true);
                                }}
                              />
                            </div>
                          </>
                        )}

                      {/* Agendar Manutenção */}
                      <div className="linha" />
                      <div className="flex space-between no-wrap">
                        <span className="fonteTexto">Agendar manutenção</span>
                        <ToggleConfigurar
                          disabled={replicado}
                          nome={""}
                          value={Boolean(catalogo.data_fim_manutencao)}
                          onChange={handleAtivarManutencao}
                          onConfigurar={() => {
                            setConfigsManutencao(!configsManutencao);
                          }}
                        />
                      </div>
                      <Dropdown
                        active={configsManutencao && !!catalogo.data_fim_manutencao}
                        animated={true}
                      >
                        <div className="flex">
                          {catalogo.data_fim_manutencao && (
                            <Input
                              label="Ao selecionar data e horário, seu catálogo ficará em manutenção até a data selecionada."
                              type="datetime-local"
                              disabled={replicado}
                              onChange={(newValue) => {
                                setCatalogo({
                                  ...catalogo,
                                  data_fim_manutencao: moment(
                                    newValue,
                                    "YYYY-MM-DDTHH:mm"
                                  ).format("YYYY/MM/DD HH:mm"),
                                });
                              }}
                              value={moment(
                                catalogo.data_fim_manutencao,
                                "YYYY-MM-DD HH:mm"
                              ).format("YYYY-MM-DDTHH:mm")}
                              placeholder="2023-04-27 11:28"
                            />
                          )}
                        </div>
                      </Dropdown>
                    </div>
                  </div>
                  {mostrarPainelProdutosBanners && (
                  <>
                  <div className="middle hideMobile">
                    <div className="linha" />
                  </div>
                  <div className="right">
                    <Tabs
                      tabs={[
                        { name: "Produtos | catálogo", value: "produtos" },
                        { name: "Banners", value: "banners" },
                      ]}
                      value={tabConteudo}
                      tabsNumber={3}
                      onChange={(newValue) => {
                        const proximo = newValue as "produtos" | "banners";
                        setTabConteudo(proximo);
                        // Espelha a coluna da esquerda: Produtos → Informações,
                        // Banners → Promoções.
                        setActiveTab(
                          proximo === "banners" ? "promocoes" : "informacoes"
                        );
                      }}
                    />
                    {tabConteudo === "produtos" && (
                    <div className="container-conteudo container-ativo">
                      <div className="flex fonteRotulos texto-produtos">
                        Escolha quais produtos deseja adicionar ao seu catálogo
                      </div>
                      <div className="flex">
                        <div className="flex no-wrap">
                          <div className="input">
                            <Input
                              placeholder=""
                              disabled={replicado}
                              name="produtos-catalogo"
                              value={
                                catalogo.configuracoes.adicionarTodosProdutos
                                  ? "true"
                                  : "false"
                              }
                              checked={boolean(
                                catalogo.configuracoes.adicionarTodosProdutos
                              )}
                              id="produto-todos"
                              onChange={(_, __, event) => {
                                setCatalogo({
                                  ...catalogo,
                                  configuracoes: {
                                    ...catalogo.configuracoes,
                                    adicionarTodosProdutos: true,
                                  },
                                });
                              }}
                              type="radio"
                            />
                          </div>
                          <label
                            htmlFor="produto-todos"
                            className="fonteTexto"
                            style={{
                              fontWeight: boolean(
                                catalogo.configuracoes.adicionarTodosProdutos
                              )
                                ? "bold"
                                : "normal",
                            }}
                          >
                            Todos produtos cadastrados na conta
                          </label>
                        </div>
                        <div className="flex no-wrap">
                          <div className="input">
                            <Input
                              name="produtos-catalogo"
                              disabled={replicado}
                              value={
                                catalogo.configuracoes.adicionarTodosProdutos
                                  ? catalogo.configuracoes.adicionarTodosProdutos.toString()
                                  : "false"
                              }
                              checked={
                                !boolean(
                                  catalogo.configuracoes.adicionarTodosProdutos
                                )
                              }
                              id="produto-selecionados"
                              placeholder=""
                              invalidText="Selecione pelo menos um produto"
                              isValid={{
                                isValidFunction: (value) => {
                                  return !(
                                    !boolean(
                                      catalogo.configuracoes
                                        .adicionarTodosProdutos
                                    ) && catalogo.produtos.length < 1
                                  );
                                },
                                dependencies: [
                                  catalogo.produtos,
                                  catalogo.configuracoes.adicionarTodosProdutos,
                                ],
                              }}
                              onChange={(_, __, event) => {
                                setCatalogo({
                                  ...catalogo,
                                  configuracoes: {
                                    ...catalogo.configuracoes,
                                    adicionarTodosProdutos: false,
                                  },
                                });
                              }}
                              type="radio"
                            />
                          </div>
                          <label
                            htmlFor="produto-selecionados"
                            className="fonteTexto"
                            style={{
                              fontWeight: !boolean(
                                catalogo.configuracoes.adicionarTodosProdutos
                              )
                                ? "bold"
                                : "normal",
                            }}
                          >
                            Selecionar os produtos manualmente
                          </label>
                        </div>
                      </div>
                      <div className="filtrar-produtos-container">
                        <div
                          className={`container-conteudo container-produtos no-margin ${
                            !boolean(
                              catalogo.configuracoes.adicionarTodosProdutos
                            ) && "container-ativo"
                          }`}
                        >
                          {!boolean(memoizedAdicionarTodosProdutos) && (
                            <ProdutosProvider tipoFiltro="listagem-catalogo">
                              <Produtos
                                produtosCatalogo={memoizedProdutosCatalogo}
                                onAdicionarProdutos={onAdicionarProdutos}
                                onRemoverProdutos={onRemoverProdutos}
                              />
                            </ProdutosProvider>
                          )}
                        </div>
                      </div>
                    </div>
                    )}
                    {tabConteudo === "banners" && (
                      <div className="container-conteudo container-ativo">
                        <BannersCanva />
                        <BannersShowcase
                          quantidade_permitida={
                            permissoesPlanos["BANNERS_NO_CATALOGO"]?.ilimitado
                              ? limiteDeBanners
                              : permissoesPlanos["BANNERS_NO_CATALOGO"]
                                  ?.limitador || limiteDeBanners
                          }
                          disabled={replicado}
                        />
                      </div>
                    )}
                  </div>
                  </>
                  )}
                  {editandoProdutosPromocao && (
                    <>
                      <div className="middle hideMobile">
                        <div className="linha" />
                      </div>
                      <div className="right">
                        <Tabs
                          tabs={[
                            { name: "Produtos | promoção", value: "produtos-promocao" },
                            { name: "Banners", value: "banners" },
                          ]}
                          value={tabPromocao}
                          tabsNumber={3}
                          onChange={(newValue) =>
                            setTabPromocao(
                              newValue as "produtos-promocao" | "banners"
                            )
                          }
                        />
                        {tabPromocao === "produtos-promocao" && (
                        <div className="container-conteudo container-ativo">
                          <ProdutosProvider tipoFiltro="listagem-promocao" idsPromocaoEditando={produtosPromocaoAtual}>
                            <ProdutosPromocao
                              editandoIndex={editandoAjusteProdutoIndex}
                              produtosCatalogo={memoizedProdutosCatalogo}
                              produtosPromocaoAtual={produtosPromocaoAtual}
                              produtosEmOutraPromocao={produtosEmPromocao}
                              variacoesEmOutraPromocao={variacoesEmOutraPromocao}
                              variacoesPorProduto={variacoesPromocaoAtual}
                              onAdicionar={(id) => {
                                setProdutosPromocaoAtual([...produtosPromocaoAtual, id]);
                              }}
                              onRemover={(id) => {
                                setProdutosPromocaoAtual(produtosPromocaoAtual.filter(p => p !== id));
                                setVariacoesPromocaoAtual((atual) => {
                                  const { [id]: _removido, ...resto } = atual;
                                  return resto;
                                });
                              }}
                              onAdicionarTodos={(ids) => {
                                // Adicionar produtos que não estão em outra promoção
                                const novos = ids.filter(
                                  id => !produtosPromocaoAtual.includes(id)
                                );
                                setProdutosPromocaoAtual([...produtosPromocaoAtual, ...novos]);
                              }}
                              onRemoverTodos={() => {
                                setProdutosPromocaoAtual([]);
                                setVariacoesPromocaoAtual({});
                              }}
                              onAlterarVariacoes={(id_produto, variacoes) => {
                                setVariacoesPromocaoAtual((atual) => {
                                  if (!variacoes.length) {
                                    const { [id_produto]: _removido, ...resto } = atual;
                                    return resto;
                                  }
                                  return { ...atual, [id_produto]: variacoes };
                                });
                              }}
                            />
                          </ProdutosProvider>
                        </div>
                        )}
                        {tabPromocao === "banners" && (
                          <div className="container-conteudo container-ativo">
                            <BannersCanva />
                            <BannersShowcase
                              quantidade_permitida={
                                permissoesPlanos["BANNERS_NO_CATALOGO"]?.ilimitado
                                  ? limiteDeBanners
                                  : permissoesPlanos["BANNERS_NO_CATALOGO"]
                                      ?.limitador || limiteDeBanners
                              }
                              disabled={replicado}
                            />
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <FooterPaginas>
                    <div className="footer-info">
                      <span className="fonteRotulos">
                        Catálogo {catalogo.descricao} | Tabela de preço {tabelaSelecionada?.descricao}
                      </span>
                      <span className="fonteRotulos">
                        {boolean(catalogo.configuracoes.adicionarTodosProdutos)
                          ? "Todos produtos no catálogo"
                          : `${catalogo.produtos.length} produtos no catálogo`} | {produtosComPromocao} produtos com promoção
                      </span>
                    </div>
                    <div className="footer-wrapper">
                      <div
                        className={`fonteTexto green ${
                          catalogo.id === "0" && "hideMobile"
                        }`}
                      >
                        {catalogo.id !== "0" &&
                          boolean(catalogo.principal) === false &&
                          !catalogo.duplicado &&
                          catalogo.pode_excluir !== false && (
                            <div onClick={() => handleExcluirCatalogo()}>
                              Excluir
                            </div>
                          )}
                      </div>
                      <div className="right-flex">
                        {catalogo.id !== "0" && !catalogo.duplicado && catalogo.pode_criar !== false && (
                          <Link
                            to={`/painel/catalogos/catalogo/${
                              catalogo.id || "0"
                            }/${!catalogo.descricao || /^\.+$/.test(catalogo.descricao) ? '_' + (catalogo.descricao || '') : encodeURIComponent(catalogo.descricao).replace(/\./g, '%2E')}/duplicado`}
                          >
                            <div
                              className="fonteTexto green"
                            >
                              Duplicar catálogo
                            </div>
                          </Link>
                        )}
                        <a href="#avancado">
                          <Button
                            disabled={replicado}
                            className="button cinza fonteTexto"
                            title="Configurações avançadas"
                            onClick={() => {
                              const newTabs = [...tabs];
                              if (newTabs.length === 2) {
                                newTabs.push({
                                  name: "Avançado",
                                  value: "avancado",
                                });
                                setActiveTab("avancado");
                              }
                              setTabs(newTabs);
                            }}
                          />
                        </a>
                        <Button
                          className={`fonteTexto button ${!formValid && "disabled"}`}
                          type="submit"
                          title="Salvar e continuar"
                          disabled={replicado}
                          loading={loadingSalvar}
                        />
                      </div>
                    </div>
                  </FooterPaginas>
                </>
              </div>
            </Form>
          </>
        )}
      </ContainerCatalogo>
    </>
  );
};

export default Catalogos;
